import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Info, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const HeroCarousel = ({ movies, onShowDetails }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  const currentMovie = movies[currentIndex];

  // Auto-advance carousel
  useEffect(() => {
    if (isPaused) return;

    timerRef.current = setInterval(() => {
      goToNext();
    }, 8000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentIndex, isPaused]);

  // Navigation handlers
  const goToSlide = useCallback((index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 800);
  }, [isAnimating, currentIndex]);

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % movies.length);
    setTimeout(() => setIsAnimating(false), 800);
  }, [isAnimating, movies.length]);

  const goToPrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + movies.length) % movies.length);
    setTimeout(() => setIsAnimating(false), 800);
  }, [isAnimating, movies.length]);

  const handleShowDetails = useCallback(() => {
    onShowDetails?.(currentMovie);
  }, [currentMovie, onShowDetails]);

  if (!movies || movies.length === 0) return null;

  return (
    <section
      ref={containerRef}
      className="relative w-full h-screen overflow-hidden bg-background"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Images */}
      {movies.map((movie, index) => (
        <div
          key={movie.id}
          className={cn(
            'absolute inset-0 transition-all duration-1000 ease-premium',
            index === currentIndex
              ? 'opacity-100 scale-100 z-10'  // Reduced z-index, but needs to be distinct.
              : 'opacity-0 scale-105 z-0'
          )}
        >
          {/* Backdrop Image with Slow Ken Burns Effect */}
          <div
            className={cn(
              'absolute inset-0 bg-cover bg-center transform-gpu',
              index === currentIndex && 'animate-ken-burns'
            )}
            style={{
              backgroundImage: `url(${movie.backdrop})`,
            }}
          />

          {/* Gradient Overlays - Updated to use Theme Background (Navy) */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent" />

          {/* Content Container */}
        </div>
      ))}

      {/* Content Container */}
      <div className="absolute inset-0 z-20 flex flex-col justify-end md:justify-center px-4 sm:px-6 lg:px-16 pb-32 md:pb-0 pointer-events-none">
        <div className="w-full max-w-[1920px] mx-auto pointer-events-auto">
          <div className="max-w-2xl lg:max-w-3xl relative">
            {/* Movie Info */}
            {movies.map((movie, index) => (
              <div
                key={movie.id}
                className={cn(
                  'transition-all duration-700 w-full',
                  // Mobile: Relative positioning, stacked
                  'relative md:absolute md:top-1/2 md:-translate-y-1/2 md:left-0',
                  index === currentIndex
                    ? 'opacity-100 translate-y-0' // Fade in and slide up
                    : 'opacity-0 translate-y-8 absolute bottom-0 left-0 right-0 pointer-events-none' // Hide others nicely
                )}
              >
                {/* Title */}
                <h1
                  className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black text-white mb-2 md:mb-4 leading-none tracking-tighter text-shadow-lg opacity-0 animate-fade-in-up"
                  style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
                >
                  {movie.title}
                </h1>

                {/* Metadata / Info Row */}
                <div
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 md:mb-6 opacity-0 animate-fade-in text-xs sm:text-sm md:text-base font-medium text-gray-200"
                  style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
                >
                  <span className="text-green-500 font-bold">{movie.match || '98% Match'}</span>
                  <span>{movie.year}</span>
                  <span className="px-2 py-0.5 border border-gray-400 rounded text-[10px] md:text-xs uppercase">{movie.maturity}</span>
                  <span>{movie.duration}</span>
                  <div className="hidden sm:flex items-center gap-2">
                    {movie.genres.slice(0, 3).map((genre, idx) => (
                      <span key={idx} className="text-gray-300">
                        {idx > 0 && <span className="mx-2">•</span>}
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Description (Hidden on very small mobile to save space, visible on sm+) */}
                <p
                  className="text-sm md:text-lg text-gray-300 line-clamp-2 md:line-clamp-3 mb-6 max-w-xl opacity-0 animate-fade-in-up leading-relaxed drop-shadow-md hidden sm:block"
                  style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}
                >
                  {movie.description}
                </p>

                {/* CTA Buttons */}
                <div
                  className="flex flex-row items-center gap-3 opacity-0 animate-fade-in-up pb-1"
                  style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}
                >
                  <Button
                    onClick={handleShowDetails}
                    size="lg"
                    className="h-10 px-6 sm:h-14 sm:px-8 bg-primary text-black hover:bg-primary/90 font-bold text-sm sm:text-lg rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl"
                  >
                    <Play className="w-4 h-4 sm:w-6 sm:h-6 fill-current mr-2" />
                    VER AHORA
                  </Button>

                  <Button
                    onClick={handleShowDetails}
                    variant="outline"
                    size="lg"
                    className="h-10 px-6 sm:h-14 sm:px-8 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:border-white font-semibold text-sm sm:text-lg rounded-full backdrop-blur-sm transition-all duration-300"
                  >
                    <Info className="w-4 h-4 sm:w-6 sm:h-6 mr-2" />
                    MÁS INFO
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {movies.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={cn(
              'h-2 rounded-full transition-all duration-300 shadow-md',
              index === currentIndex
                ? 'w-8 bg-primary'
                : 'w-2 bg-white/50 hover:bg-white'
            )}
          />
        ))}
      </div>

      {/* Mute Button - Bottom Right */}
      <div className="absolute bottom-8 right-8 z-20">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMuted(!isMuted)}
          className="w-10 h-10 rounded-full border border-white/20 bg-black/30 backdrop-blur-sm text-white hover:bg-white/10"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Volume2 className="w-5 h-5" />
          )}
        </Button>
      </div>

      {/* Side Navigation Arrows */}
      <Button
        variant="ghost"
        size="icon"
        onClick={goToPrev}
        className={cn(
          'absolute left-4 top-1/2 -translate-y-1/2 z-10',
          'w-12 h-12 md:w-14 md:h-14 rounded-full',
          'bg-background/30 hover:bg-background/60 backdrop-blur-sm',
          'text-foreground border border-foreground/20',
          'transition-all duration-300 opacity-0 hover:opacity-100',
          'hidden md:flex'
        )}
      >
        <ChevronLeft className="w-8 h-8" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        onClick={goToNext}
        className={cn(
          'absolute right-4 top-1/2 -translate-y-1/2 z-10',
          'w-12 h-12 md:w-14 md:h-14 rounded-full',
          'bg-background/30 hover:bg-background/60 backdrop-blur-sm',
          'text-foreground border border-foreground/20',
          'transition-all duration-300 opacity-0 hover:opacity-100',
          'hidden md:flex'
        )}
      >
        <ChevronRight className="w-8 h-8" />
      </Button>
    </section >
  );
};

export default HeroCarousel;
